// src/pages/Archive.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tab,
  Tabs,
  Checkbox,
  FormControlLabel,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  FilterList,
  MoreVert,
  Download,
  Refresh,
  Search,
  Archive as ArchiveIcon,
  Visibility,
  Delete,
  PictureAsPdf,
  TableChart,
  ExpandMore,
  BarChart,
  Timeline,
  DateRange,
  Category
} from '@mui/icons-material';
import { useI18n } from '../i18n/i18n';
import { loadMeterData, loadShiftReports, loadTechnicalReports, loadAllRawRecords } from '../utils/dataLoader';

const Archive = () => {
  const { t } = useI18n();
  
  // Додаємо функцію для fallback перекладів
  const tt = (key, fallback) => {
    const translation = t(key);
    return translation === key ? fallback : translation;
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    type: 'all',
    dateFrom: '',
    dateTo: '',
    status: 'all',
    search: ''
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('excel');

  const tabs = [
    { label: tt('archive.tabs.operator', 'Reportes Operador'), value: 'operator' },
    { label: tt('archive.tabs.technician', 'Reportes Técnico'), value: 'technician' },
    { label: tt('archive.tabs.meters', 'Contadores'), value: 'meters' },
    { label: tt('archive.tabs.misc', 'Otros'), value: 'misc' }
  ];

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const allRecords = await loadAllRawRecords();
      
      const allData = [
        ...allRecords.meterData.map(item => ({ 
          ...item, 
          type: 'meter', 
          id: `meter_${item.id}`,
          category: 'meters'
        })),
        ...allRecords.shiftReports.map(item => ({ 
          ...item, 
          type: 'shift', 
          id: `shift_${item.id}`,
          category: 'operator'
        })),
        ...allRecords.technicalReports.map(item => ({ 
          ...item, 
          type: 'technical', 
          id: `tech_${item.id}`,
          category: 'technician'
        }))
      ];

      setData(allData);
      setFilteredData(allData);
    } catch (err) {
      setError(tt('archive.loadError', 'Error cargando datos'));
      console.error('Error cargando:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let result = data;

    // Filter by tab
    if (currentTab !== 0) {
      const tabValue = tabs[currentTab].value;
      result = result.filter(item => item.category === tabValue);
    }

    // Filter by type
    if (filters.type !== 'all') {
      result = result.filter(item => item.type === filters.type);
    }

    // Filter by date range
    if (filters.dateFrom) {
      result = result.filter(item => new Date(item.date) >= new Date(filters.dateFrom));
    }

    if (filters.dateTo) {
      result = result.filter(item => new Date(item.date) <= new Date(filters.dateTo));
    }

    // Filter by status
    if (filters.status !== 'all') {
      result = result.filter(item => item.status === filters.status);
    }

    // Filter by search
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(item =>
        JSON.stringify(item).toLowerCase().includes(searchLower) ||
        (item.id && item.id.toLowerCase().includes(searchLower)) ||
        (item.operator && item.operator.toLowerCase().includes(searchLower)) ||
        (item.client && item.client.toLowerCase().includes(searchLower))
      );
    }

    setFilteredData(result);
    setCurrentPage(1);
  }, [data, filters, currentTab, tabs]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMenuOpen = (event, record) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecord(record);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRecord(null);
  };

  const handleRecordSelect = (recordId) => {
    setSelectedRecords(prev =>
      prev.includes(recordId)
        ? prev.filter(id => id !== recordId)
        : [...prev, recordId]
    );
  };

  const handleSelectAll = () => {
    if (selectedRecords.length === currentPageData.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(currentPageData.map(item => item.id));
    }
  };

  const handleViewRecord = () => {
    if (selectedRecord) {
      setViewDialogOpen(true);
      handleMenuClose();
    }
  };

  const handleExport = () => {
    const dataToExport = selectedRecords.length > 0 
      ? filteredData.filter(item => selectedRecords.includes(item.id))
      : filteredData;

    if (exportFormat === 'excel') {
      exportToExcel(dataToExport);
    } else {
      exportToPDF(dataToExport);
    }
    setExportDialogOpen(false);
  };

  const exportToExcel = (data) => {
    const csvContent = [
      ['ID', 'Type', 'Date', 'Status', 'Operator', 'Client', 'Data'].join(','),
      ...data.map(item => [
        item.id,
        item.type,
        item.date,
        item.status,
        item.operator || '',
        item.client || '',
        JSON.stringify(item.data || {})
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `archive_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportToPDF = (data) => {
    // Simulate PDF export
    alert(`Exporting ${data.length} records to PDF...`);
    console.log('PDF Export Data:', data);
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'success',
      pending: 'warning',
      error: 'error',
      active: 'info'
    };
    return colors[status] || 'default';
  };

  const getTypeLabel = (type) => {
    const labels = {
      meter: tt('archive.type.meter', 'Medidores'),
      shift: tt('archive.type.shift', 'Turnos'),
      technical: tt('archive.type.technical', 'Técnicos')
    };
    return labels[type] || type;
  };

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentPageData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ArchiveIcon />
        {tt('archive.title', 'Archivo')}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" paragraph>
        {tt('archive.subtitle', 'Vista centralizada y gestión de todos los tipos de informes')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      {/* Filters */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList />
            <Typography>Filtros y Búsqueda</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{tt('archive.filters.type', 'Tipo')}</InputLabel>
                <Select
                  value={filters.type}
                  label={tt('archive.filters.type', 'Tipo')}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                >
                  <MenuItem value="all">{tt('archive.filters.all', 'Todos')}</MenuItem>
                  <MenuItem value="meter">{tt('archive.type.meter', 'Medidores')}</MenuItem>
                  <MenuItem value="shift">{tt('archive.type.shift', 'Turnos')}</MenuItem>
                  <MenuItem value="technical">{tt('archive.type.technical', 'Técnicos')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={tt('archive.filters.dateFrom', 'Desde fecha')}
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={tt('archive.filters.dateTo', 'Hasta fecha')}
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{tt('archive.filters.status', 'Estado')}</InputLabel>
                <Select
                  value={filters.status}
                  label={tt('archive.filters.status', 'Estado')}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="all">{tt('archive.filters.all', 'Todos')}</MenuItem>
                  <MenuItem value="completed">{tt('archive.status.completed', 'Completado')}</MenuItem>
                  <MenuItem value="pending">{tt('archive.status.pending', 'Pendiente')}</MenuItem>
                  <MenuItem value="error">{tt('archive.status.error', 'Error')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label={tt('archive.search_placeholder', 'Buscar en reportes...')}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={() => setExportDialogOpen(true)}
                disabled={filteredData.length === 0}
              >
                {tt('archive.export', 'Exportar')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadData}
              >
                {tt('archive.refresh', 'Actualizar')}
              </Button>
            </Box>
            <Button
              variant="text"
              onClick={() => {
                setFilters({
                  type: 'all',
                  dateFrom: '',
                  dateTo: '',
                  status: 'all',
                  search: ''
                });
              }}
            >
              {tt('actions_clear_filters', 'Limpiar Filtros')}
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Results Summary */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {tt('archive.results', 'Resultados')} ({filteredData.length})
            </Typography>
            {selectedRecords.length > 0 && (
              <Typography variant="body2" color="primary">
                {tt('archive.selected', 'Seleccionados')}: {selectedRecords.length}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedRecords.length > 0 && selectedRecords.length < currentPageData.length}
                      checked={selectedRecords.length === currentPageData.length && currentPageData.length > 0}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>{tt('archive.table.id', 'ID')}</TableCell>
                  <TableCell>{tt('archive.table.type', 'Tipo')}</TableCell>
                  <TableCell>{tt('archive.table.date', 'Fecha')}</TableCell>
                  <TableCell>{tt('archive.table.status', 'Estado')}</TableCell>
                  <TableCell>Operador</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>{tt('archive.table.actions', 'Acciones')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentPageData.map((record) => (
                  <TableRow 
                    key={record.id}
                    sx={{ 
                      '&:hover': { backgroundColor: 'action.hover' },
                      backgroundColor: selectedRecords.includes(record.id) ? 'action.selected' : 'inherit'
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRecords.includes(record.id)}
                        onChange={() => handleRecordSelect(record.id)}
                      />
                    </TableCell>
                    <TableCell>{record.id}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getTypeLabel(record.type)} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(record.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={tt(`archive.status.${record.status}`, record.status)}
                        color={getStatusColor(record.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{record.operator || '-'}</TableCell>
                    <TableCell>{record.client || '-'}</TableCell>
                    <TableCell>
                      <Tooltip title={tt('archive.actions.more', 'Acciones')}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, record)}
                        >
                          <MoreVert />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredData.length === 0 && !loading && (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">
                {tt('archive.noData', 'Datos no encontrados')}
              </Typography>
            </Box>
          )}

          {/* Pagination */}
          {filteredData.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, page) => setCurrentPage(page)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewRecord}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          {tt('archive.actions.view', 'Ver')}
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <Download fontSize="small" />
          </ListItemIcon>
          {tt('archive.actions.download', 'Descargar')}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          {tt('archive.actions.delete', 'Eliminar')}
        </MenuItem>
      </Menu>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
        <DialogTitle>Exportar Datos</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Exportar {selectedRecords.length > 0 ? selectedRecords.length : filteredData.length} registros
          </Typography>
          <FormControl component="fieldset">
            <FormControlLabel
              control={
                <Checkbox 
                  checked={exportFormat === 'excel'} 
                  onChange={() => setExportFormat('excel')} 
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TableChart />
                  Excel (CSV)
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox 
                  checked={exportFormat === 'pdf'} 
                  onChange={() => setExportFormat('pdf')} 
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PictureAsPdf />
                  PDF
                </Box>
              }
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleExport} variant="contained">
            Exportar
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Record Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalles del Registro
        </DialogTitle>
        <DialogContent>
          {selectedRecord && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedRecord.id}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Tipo:
                  </Typography>
                  <Typography>{getTypeLabel(selectedRecord.type)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Fecha:
                  </Typography>
                  <Typography>{new Date(selectedRecord.date).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Estado:
                  </Typography>
                  <Chip 
                    label={tt(`archive.status.${selectedRecord.status}`, selectedRecord.status)}
                    color={getStatusColor(selectedRecord.status)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Operador:
                  </Typography>
                  <Typography>{selectedRecord.operator || '-'}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Datos:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                  {JSON.stringify(selectedRecord.data || selectedRecord, null, 2)}
                </pre>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Archive;


